import {
  NEWS_CTA_URL_PATTERN,
  NEWS_IMAGE_MAX_SIZE_BYTES,
  NEWS_IMAGE_MIME_TYPES,
  NEWS_SLUG_MAX_LENGTH,
  NEWS_VIDEO_EXTENSIONS,
  NEWS_VIDEO_MAX_SIZE_BYTES,
  NEWS_VIDEO_MIME_TYPES,
} from '../constants/news.constants';

/**
 * Message d'une erreur de l'API, prêt à afficher. NestJS renvoie `message` en
 * chaîne (400/409 levés par le service) ou en tableau (erreurs de validation des
 * DTO, une par champ) : on joint ce dernier plutôt que de n'en montrer qu'une.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as any)?.response?.data?.message;
  if (Array.isArray(message)) {
    const parts = message.filter((part) => typeof part === 'string' && part.trim());
    if (parts.length > 0) return parts.join(' ');
  }
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

/**
 * Contrôle local d'une image avant téléversement — mêmes règles que le serveur
 * (JPEG, PNG ou WebP, 5 Mo), pour éviter d'envoyer 20 Mo et d'attendre un refus.
 * Renvoie le message d'erreur, ou `null` si le fichier est acceptable.
 */
export function validateNewsImageFile(file: File): string | null {
  if (!NEWS_IMAGE_MIME_TYPES.includes(file.type)) {
    return "Format d'image non pris en charge : utilisez un fichier JPEG, PNG ou WebP.";
  }
  if (file.size > NEWS_IMAGE_MAX_SIZE_BYTES) {
    return "L'image dépasse 5 Mo. Réduisez-la avant de l'envoyer.";
  }
  return null;
}

/**
 * Contrôle local d'une vidéo avant téléversement : MP4 ou WebM, 100 Mo au plus.
 * Évite d'envoyer plusieurs centaines de Mo pour recevoir un refus. Le navigateur
 * ne renseigne pas toujours le type d'un fichier : à défaut, on se fie à
 * l'extension. Le serveur, lui, tranche sur le contenu du fichier.
 * Renvoie le message d'erreur, ou `null` si le fichier est acceptable.
 */
export function validateNewsVideoFile(file: File): string | null {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const typeAccepted = file.type
    ? NEWS_VIDEO_MIME_TYPES.includes(file.type)
    : NEWS_VIDEO_EXTENSIONS.includes(extension);
  if (!typeAccepted) {
    return 'Format de vidéo non pris en charge : seuls les fichiers MP4 et WebM sont acceptés. Convertissez la vidéo (un fichier .mov ou .avi, par exemple) en MP4 avant de l\'envoyer.';
  }
  if (file.size > NEWS_VIDEO_MAX_SIZE_BYTES) {
    const sizeMb = Math.ceil(file.size / (1024 * 1024));
    return `La vidéo pèse ${sizeMb} Mo : le maximum est de 100 Mo. Compressez-la ou raccourcissez-la avant de l'envoyer.`;
  }
  return null;
}

/**
 * Nettoyage d'un slug PENDANT la saisie : minuscules, sans accents, tout
 * caractère interdit devient un tiret. Un tiret final est conservé — sinon il
 * serait impossible de taper « garantie-24 » — et retiré à la sortie du champ
 * par `trimSlug`. La forme définitive reste celle que renvoie l'API.
 */
export function sanitizeSlugInput(input: string): string {
  return input
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, NEWS_SLUG_MAX_LENGTH);
}

export function trimSlug(slug: string): string {
  return slug.replace(/^-+|-+$/g, '');
}

/**
 * Identifiant d'une vidéo YouTube à partir d'un lien collé par le rédacteur :
 * page de lecture (`watch?v=`), lien court (`youtu.be/`), Shorts, direct, ou
 * lien d'intégration. `null` si le lien n'est pas reconnu.
 */
export function parseYoutubeVideoId(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;

  let url: URL;
  try {
    url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^(www\.|m\.|music\.)/, '');
  let id: string | null = null;
  if (host === 'youtu.be') {
    id = url.pathname.split('/')[1] ?? null;
  } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (url.pathname === '/watch') {
      id = url.searchParams.get('v');
    } else {
      const match = url.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/]+)/);
      id = match ? match[1] : null;
    }
  }

  return id && /^[\w-]{6,20}$/.test(id) ? id : null;
}

/**
 * Le corps est-il vide pour un lecteur ? Un éditeur vidé laisse `<p></p>` : la
 * chaîne n'est pas vide, mais il n'y a rien à lire. Une image ou une vidéo seule
 * compte comme du contenu.
 */
export function isNewsBodyEmpty(html: string): boolean {
  if (/<(img|iframe|video)\b/i.test(html)) return false;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
}

/** Le corps contient-il du TEXTE (et pas seulement des images ou des vidéos) ? */
export function newsBodyHasText(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';
}

/** Date d'une nouveauté en toutes lettres (« 5 octobre 2026 »), ou `null` si elle est absente ou illisible. */
export function formatNewsDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export interface NewsCta {
  label: string;
  url: string;
  /** `true` : adresse http(s), à ouvrir dans un nouvel onglet. `false` : chemin interne de l'application. */
  external: boolean;
}

/**
 * Bouton d'action d'une nouveauté, ou `null` s'il n'y en a pas. Le lien finit
 * dans un `href` et, pour un chemin interne, dans `navigate()` : on ne rend le
 * bouton que pour une adresse http(s) ou un chemin interne conformes au motif
 * de l'API, jamais pour un autre schéma.
 *
 * Un lien enregistré avant le durcissement du motif et qui ne le respecte plus
 * (« /\evil.com ») donne donc `null` : pas de bouton, pas de navigation — plutôt
 * que de le traiter comme un chemin interne qui sortirait du domaine.
 */
export function getNewsCta(news: { ctaLabel?: string | null; ctaUrl?: string | null }): NewsCta | null {
  const { ctaLabel, ctaUrl } = news;
  if (!ctaLabel || !ctaUrl || !NEWS_CTA_URL_PATTERN.test(ctaUrl)) return null;
  return { label: ctaLabel, url: ctaUrl, external: /^https?:\/\//i.test(ctaUrl) };
}
